import { logError } from "@/lib/error-handling";
import { apiClient } from "../api";
import type { HubSpotUserSyncScope } from "@/types/hubspot-user-sync";
import type { HubSpotCompanySyncScope } from "@/types/hubspot-company-sync";
import type { UserCreationMode } from "@/types/salesforce-user-sync";
import type { InstallationStatus } from "@/lib/hubspot/installation-status";
import type {
  CompanyFieldMetadata,
  CompanySyncFilterState,
} from "@/lib/hubspot/company-filter";
import type { ContactSyncFilterState } from "@/lib/hubspot/contact-filter";
import type { CompanyFilterInputBody } from "@/schemas/hubspotCompanyFilterSchema";
import type { ContactFilterInputBody } from "@/schemas/hubspotContactFilterSchema";
import type { FilterCountInputBody } from "@/schemas/hubspotFilterCountSchema";
import type { CompanyFilterChangeResult } from "@/services/backendServices/hubspot/HubSpotCompanyFilterService";
import type { ContactFilterChangeResult } from "@/services/backendServices/hubspot/HubSpotContactFilterService";
import type { SweepView } from "@/services/backendServices/hubspot/HubSpotCompanyFilterSweepService";

export interface SaveHubSpotUserSyncConfigParams {
  scope: HubSpotUserSyncScope;
  creation_mode?: UserCreationMode;
  /** HubSpot owner ids; required and non-empty when scope is SELECTED. */
  selected_user_ids?: string[];
}

/**
 * Outcome of the full users read the PATCH triggers when the selection
 * changes. Absent when nothing was triggered.
 */
export type HubSpotUsersReadOutcome =
  | { triggered: true }
  | { triggered: false; code: string; message: string };

export interface SaveHubSpotUserSyncConfigResponse {
  success: boolean;
  message: string;
  sync?: HubSpotUsersReadOutcome;
}

/** A HubSpot portal user offered in the user sync pick-list. */
export interface HubSpotPickListUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  /** Already a Skrift member linked to this HubSpot owner id. */
  inSkrift: boolean;
  /** That member is currently synced from HubSpot. */
  syncActive: boolean;
}

export interface HubSpotPickListResponse {
  success: boolean;
  scope: HubSpotUserSyncScope;
  selected_user_ids: string[];
  /** The portal has more users than the pick-list loads. */
  truncated: boolean;
  users: HubSpotPickListUser[];
}

export async function fetchHubSpotPickListUsers() {
  try {
    return await apiClient<HubSpotPickListResponse>("/api/hubspot/users");
  } catch (error) {
    logError(error, "fetchHubSpotPickListUsers");
    throw error;
  }
}

/** A member who stops syncing if a SELECTED list is saved. */
export interface HubSpotMemberLeavingSync {
  id: string;
  name: string;
}

/** Members a SELECTED list would switch off, read from Skrift's database. */
export async function fetchHubSpotMembersLeavingSync(
  selectedUserIds: string[]
) {
  try {
    return await apiClient<{
      success: boolean;
      users: HubSpotMemberLeavingSync[];
    }>("/api/hubspot/users/leaving-sync", {
      method: "POST",
      body: JSON.stringify({ selected_user_ids: selectedUserIds }),
    });
  } catch (error) {
    logError(error, "fetchHubSpotMembersLeavingSync");
    throw error;
  }
}

export type SaveHubSpotCompanySyncConfigParams = CompanyFilterInputBody;

export interface SaveHubSpotFieldMappingsParams {
  mappings: Array<{
    providerField: string;
    skriftField: string;
    category?: "required" | "recommended" | "additional";
  }>;
}

export async function saveHubSpotUserSyncConfig(
  params: SaveHubSpotUserSyncConfigParams
) {
  try {
    return await apiClient<{ success: boolean; message: string }>(
      "/api/hubspot/save-user-sync-config",
      {
        method: "POST",
        body: JSON.stringify(params),
      }
    );
  } catch (error) {
    logError(error, "saveHubSpotUserSyncConfig");
    throw error;
  }
}

export async function updateHubSpotUserSyncConfig(
  params: SaveHubSpotUserSyncConfigParams
) {
  try {
    return await apiClient<SaveHubSpotUserSyncConfigResponse>(
      "/api/hubspot/save-user-sync-config",
      {
        method: "PATCH",
        body: JSON.stringify(params),
      }
    );
  } catch (error) {
    logError(error, "updateHubSpotUserSyncConfig");
    throw error;
  }
}

export async function saveHubSpotCompanySyncConfig(
  params: SaveHubSpotCompanySyncConfigParams
) {
  try {
    return await apiClient<{
      success: boolean;
      message: string;
      config: { scope: HubSpotCompanySyncScope };
    }>("/api/hubspot/save-company-sync-config", {
      method: "POST",
      body: JSON.stringify(params),
    });
  } catch (error) {
    logError(error, "saveHubSpotCompanySyncConfig");
    throw error;
  }
}

export async function saveHubSpotContactSyncConfig(
  params: ContactFilterInputBody
) {
  try {
    return await apiClient<{
      success: boolean;
      message: string;
      config: { scope: ContactSyncFilterState["scope"] };
    }>("/api/hubspot/save-contact-sync-config", {
      method: "POST",
      body: JSON.stringify(params),
    });
  } catch (error) {
    logError(error, "saveHubSpotContactSyncConfig");
    throw error;
  }
}

export async function saveHubSpotFieldMappings(
  params: SaveHubSpotFieldMappingsParams
) {
  try {
    return await apiClient<{ success: boolean; message: string }>(
      "/api/hubspot/save-field-mappings",
      {
        method: "POST",
        body: JSON.stringify(params),
      }
    );
  } catch (error) {
    logError(error, "saveHubSpotFieldMappings");
    throw error;
  }
}

export async function updateHubSpotFieldMappings(
  params: SaveHubSpotFieldMappingsParams
) {
  try {
    return await apiClient<{ success: boolean; message: string }>(
      "/api/hubspot/save-field-mappings",
      {
        method: "PATCH",
        body: JSON.stringify(params),
      }
    );
  } catch (error) {
    logError(error, "updateHubSpotFieldMappings");
    throw error;
  }
}

export async function fetchHubSpotInstallationStatus(): Promise<InstallationStatus> {
  try {
    return await apiClient<InstallationStatus>(
      "/api/hubspot/installation-status"
    );
  } catch (error) {
    logError(error, "fetchHubSpotInstallationStatus");
    throw error;
  }
}

export async function reapplyHubSpotCompanyFilter(): Promise<{
  success: boolean;
  result:
    | "patched"
    | "unchanged"
    | "no_installation"
    | "no_account"
    | "malformed";
}> {
  try {
    return await apiClient<{
      success: boolean;
      result:
        | "patched"
        | "unchanged"
        | "no_installation"
        | "no_account"
        | "malformed";
    }>("/api/hubspot/company-filter/reapply", { method: "POST" });
  } catch (error) {
    logError(error, "reapplyHubSpotCompanyFilter");
    throw error;
  }
}

export async function fetchHubSpotCompanyFilter(): Promise<{
  state: CompanySyncFilterState;
  sweep: SweepView | null;
}> {
  try {
    return await apiClient<{
      state: CompanySyncFilterState;
      sweep: SweepView | null;
    }>("/api/hubspot/company-filter");
  } catch (error) {
    logError(error, "fetchHubSpotCompanyFilter");
    throw error;
  }
}

export async function changeHubSpotCompanyFilter(
  body: CompanyFilterInputBody
): Promise<CompanyFilterChangeResult> {
  try {
    return await apiClient<CompanyFilterChangeResult>(
      "/api/hubspot/company-filter",
      { method: "PUT", body: JSON.stringify(body) }
    );
  } catch (error) {
    logError(error, "changeHubSpotCompanyFilter");
    throw error;
  }
}

export async function applyHubSpotCompanyFilterSweep(body: {
  sweepId: string;
  allowEmptyRead?: boolean;
}): Promise<{ sweep: SweepView | null }> {
  try {
    return await apiClient<{ sweep: SweepView | null }>(
      "/api/hubspot/company-filter/sweep/apply",
      { method: "POST", body: JSON.stringify(body) }
    );
  } catch (error) {
    logError(error, "applyHubSpotCompanyFilterSweep");
    throw error;
  }
}

export interface HubSpotContactFilterData {
  state: ContactSyncFilterState;
  sweep: SweepView | null;
  /** The org's HubSpot contact properties; best-effort. */
  properties: CompanyFieldMetadata[];
  /** True when the properties failed to load (vs. a genuinely empty list). */
  propertiesError: boolean;
}

export async function fetchHubSpotContactFilter(): Promise<HubSpotContactFilterData> {
  try {
    return await apiClient<HubSpotContactFilterData>(
      "/api/hubspot/contact-filter"
    );
  } catch (error) {
    logError(error, "fetchHubSpotContactFilter");
    throw error;
  }
}

export async function changeHubSpotContactFilter(
  body: ContactFilterInputBody
): Promise<ContactFilterChangeResult> {
  try {
    return await apiClient<ContactFilterChangeResult>(
      "/api/hubspot/contact-filter",
      { method: "PUT", body: JSON.stringify(body) }
    );
  } catch (error) {
    logError(error, "changeHubSpotContactFilter");
    throw error;
  }
}

export async function applyHubSpotContactFilterSweep(body: {
  sweepId: string;
  allowEmptyRead?: boolean;
}): Promise<{ sweep: SweepView | null }> {
  try {
    return await apiClient<{ sweep: SweepView | null }>(
      "/api/hubspot/contact-filter/sweep/apply",
      { method: "POST", body: JSON.stringify(body) }
    );
  } catch (error) {
    logError(error, "applyHubSpotContactFilterSweep");
    throw error;
  }
}

export async function disconnectHubSpot(mode: "soft" | "hard"): Promise<void> {
  try {
    await apiClient<{ success: boolean; message: string }>(
      "/api/hubspot/disconnect",
      {
        method: "POST",
        body: JSON.stringify({ mode }),
      }
    );
  } catch (error) {
    logError(error, "disconnectHubSpot");
    throw error;
  }
}

export type HubSpotFilterCount = {
  count: number;
  /** `count` is a lower bound: more companies matched than were counted. */
  capped: boolean;
};

/** Count preview for an unsaved company or contact filter (SKR-956). */
export async function fetchHubSpotFilterCount(
  body: FilterCountInputBody
): Promise<HubSpotFilterCount> {
  try {
    return await apiClient<HubSpotFilterCount>("/api/hubspot/filter-count", {
      method: "POST",
      body: JSON.stringify(body),
    });
  } catch (error) {
    logError(error, "fetchHubSpotFilterCount");
    throw error;
  }
}
